import { useState, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { Trash2, X } from 'lucide-react';

import { Janela } from './JanelaDeAcao';
import { CampoDoMotivo } from './CampoDoMotivo';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo, type OperacaoComMotivo } from '@/lib/motivo';

/**
 * Botao, nas seis variantes e nos tres tamanhos do manual (v2.1, p.21).
 *
 * Altura 40, raio 12, rotulo IBM Plex Sans 14/600 — os tres vem do utilitario
 * `botao`, em `estilos/global.css`, para nenhum deles ser redigitado aqui.
 *
 *  - `primario`   laranja 600 com rotulo branco, UM por tela: a acao que a tela
 *                 existe para provocar. Fica na ponta direita do grupo.
 *  - `secundario` branco com borda: acoes de apoio ao lado do primario.
 *  - `fantasma`   sem fundo: baixa enfase, acoes de linha e de card.
 *                 `terciario` e o nome que a versao 1.0 dava ao mesmo papel.
 *  - `consulta`   azul: consultar uma fonte — biblioteca, manual, IA. Nunca
 *                 acao de gravar.
 *  - `destrutivo` na tinta do erro: excluir, arquivar, cancelar. Sempre
 *                 confirma — ver `BotaoDestrutivo`, abaixo — e nunca e o
 *                 primario da tela. O vermelho SOLIDO e so o da confirmacao.
 *
 * Tamanhos: `p` (32, barra de filtros densa e acao de linha), `m` (40, o
 * padrao) e `g` (48, tela de acesso e toque). `icone` faz o botao redondo de
 * 40 px que leva so o icone — e entao `aria-label` e `title` sao obrigatorios,
 * porque ele "sempre tem dica ao passar".
 *
 * O rotulo e verbo + objeto, no maximo tres palavras, sem ponto final e so com
 * a primeira maiuscula: "Salvar equipamento", "Abrir O.S.". Nunca "OK",
 * "Enviar" ou "Confirmar" sozinhos. Icone a esquerda do rotulo; seta de menu a
 * direita.
 *
 * ANEL DE FOCO: nao ha propriedade para desliga-lo, e nenhuma variante escreve
 * `outline-none` (p.20).
 */
export type NivelBotao = 'primario' | 'secundario' | 'fantasma' | 'terciario' | 'destrutivo' | 'consulta';
export type TamanhoBotao = 'p' | 'm' | 'g';

const CLASSES: Record<NivelBotao, string> = {
  primario: 'botao botao-primario',
  secundario: 'botao botao-secundario',
  fantasma: 'botao botao-fantasma',
  terciario: 'botao botao-fantasma',
  destrutivo: 'botao botao-destrutivo',
  consulta: 'botao botao-consulta',
};

const TAMANHO: Record<TamanhoBotao, string> = {
  p: 'botao-p',
  m: '',
  g: 'botao-g',
};

interface PropsBotao extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly nivel?: NivelBotao;
  readonly tamanho?: TamanhoBotao;
  /** Redondo, so com o icone. Exige `aria-label` e `title`. */
  readonly icone?: boolean;
  /**
   * Carregando (p.21, R05): o anel gira antes do rotulo, na cor dele, e o
   * botao nao aceita outro clique — sem apagar a cor, como o `disabled`
   * faria: o primario continua laranja enquanto salva.
   */
  readonly carregando?: boolean;
  readonly children: ReactNode;
}

export function Botao({ nivel = 'secundario', tamanho = 'm', icone = false, carregando = false, className, children, onClick, ...resto }: PropsBotao) {
  return (
    <button
      type="button"
      className={`${CLASSES[nivel]} ${TAMANHO[tamanho]} ${icone ? 'botao-icone' : ''} ${className ?? ''}`}
      {...resto}
      {...(carregando
        ? {
            'aria-busy': true,
            'aria-disabled': true,
            /* Nem o envio do formulario: o `submit` carregando nao manda de novo. */
            onClick: (evento: MouseEvent<HTMLButtonElement>) => {
              evento.preventDefault();
            },
          }
        : onClick
          ? { onClick }
          : {})}
    >
      {children}
    </button>
  );
}

/**
 * Fechar (✕): o mesmo botao na janela, no painel, na gaveta do menu e no
 * toast — o circulo neutro de 32 px dos exemplos da p.28 e da p.29.
 */
export function BotaoFechar({
  rotulo,
  aoFechar,
}: {
  /** Nome acessivel, verbo + objeto: "Fechar painel". */
  readonly rotulo: string;
  readonly aoFechar: () => void;
}) {
  return (
    <button type="button" aria-label={rotulo} title={rotulo} className="botao-fechar" onClick={aoFechar}>
      <X aria-hidden="true" className="size-[var(--icone-controle)]" />
    </button>
  );
}

/**
 * Acao destrutiva: botao na tinta do erro + confirmacao obrigatoria.
 *
 * A confirmacao segue a p.29: o bloco do icone na tinta do erro, a pergunta
 * que NOMEIA O ALVO ("Excluir a O.S. 000124?"), o texto que diz a
 * consequencia, e dois botoes — o de saida diz o que se MANTEM ("Manter
 * O.S.", e nao "Cancelar") e o de confirmar e o unico vermelho solido do
 * sistema. Nunca "Tem certeza?": essa pergunta nao da informacao nova a quem
 * ja clicou.
 *
 * A confirmacao E A JANELA (p.29), e nao um `<dialog>` proprio: ao centro,
 * fundo escurecido a 40%, tela cheia abaixo de 768 px. E o botao de manter
 * que recebe o foco ao abrir: o Enter distraido nao destroi.
 *
 * `pedirMotivo` (06/10/2026): a operacao em que o servidor exige o motivo
 * escrito (`@/lib/motivo`). A janela ganha o campo, com o exemplo, e o botao
 * de confirmar so liga depois do piso; o motivo chega em `aoConfirmar`.
 */
export function BotaoDestrutivo({
  rotulo,
  pergunta,
  consequencia,
  rotuloConfirmar,
  rotuloCancelar = 'Manter como está',
  aoConfirmar,
  motivoIndisponivel,
  pedirMotivo,
}: {
  /** Verbo + objeto: "Excluir equipamento". */
  readonly rotulo: string;
  /** Nomeia o alvo: "Excluir o equipamento ESC-002?" */
  readonly pergunta: string;
  /** O que acontece depois. Frase completa, com ponto final. */
  readonly consequencia: string;
  readonly rotuloConfirmar: string;
  /** O que se mantem: "Manter O.S." — "Cancelar" sozinho nao diz. */
  readonly rotuloCancelar?: string;
  /** Com `pedirMotivo`, recebe o motivo escrito (sem espacos nas pontas). */
  readonly aoConfirmar?: (motivo: string) => void;
  /** Quando existe, a confirmacao abre mas o botao de confirmar fica desligado. */
  readonly motivoIndisponivel?: string;
  /** A operacao em que o motivo e obrigatorio: a janela pede o motivo. */
  readonly pedirMotivo?: OperacaoComMotivo;
}) {
  const [aberta, setAberta] = useState(false);
  const [motivo, setMotivo] = useState('');
  const faltaMotivo = pedirMotivo ? faltaNoMotivo(motivo) : null;
  const indisponivel = motivoIndisponivel ?? faltaMotivo ?? undefined;

  function fechar() {
    setAberta(false);
    setMotivo('');
  }

  function confirmar() {
    if (indisponivel) return;
    setAberta(false);
    const escrito = motivo.trim();
    setMotivo('');
    aoConfirmar?.(escrito);
  }

  return (
    <>
      <Botao
        nivel="destrutivo"
        onClick={() => {
          setAberta(true);
        }}
      >
        <Trash2 aria-hidden="true" className="size-[var(--icone-controle)]" />
        {rotulo}
      </Botao>

      <Janela
        titulo={rotulo}
        aberta={aberta}
        aoFechar={fechar}
        largura="p"
        acoes={
          <>
            <Botao nivel="secundario" onClick={fechar}>
              {rotuloCancelar}
            </Botao>
            <button
              type="button"
              className="botao botao-destrutivo-solido"
              onClick={confirmar}
              {...(indisponivel ? { disabled: true } : {})}
            >
              {rotuloConfirmar}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <span className="flex size-12 items-center justify-center rounded-controle bg-erro-fundo">
            <Trash2 aria-hidden="true" className="size-[var(--icone-destaque)] text-erro" />
          </span>
          <p className="txt-secao text-tinta">{pergunta}</p>
          <p className="txt-corpo text-pretty text-tinta-secundaria">{consequencia}</p>
          {pedirMotivo ? (
            /* R06 (p.69): o campo com o contador "42 de 10"; o exemplo no lugar vazio. */
            <CampoDoMotivo valor={motivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO[pedirMotivo]}`} aoMudar={setMotivo} />
          ) : null}
          {motivoIndisponivel ? (
            <p className="txt-apoio text-tinta-apagada">{motivoIndisponivel}</p>
          ) : null}
        </div>
      </Janela>
    </>
  );
}
